/**
 * FieldPasswordInput — drop-in replacement for `<FieldTextInput type="password" />`
 * with an inline show/hide eyeball toggle. The toggle flips the input
 * between `type="password"` and `type="text"` so users can verify
 * they're typing what they think they're typing — important on mobile
 * keyboards where there's no visual feedback otherwise, and useful on
 * any password field where typos lock the user out.
 *
 * Usage mirrors FieldTextInput. Pass all the standard props (id, name,
 * label, validate, etc.); this component handles the type internally.
 */
import React, { useState } from 'react';

import { FieldTextInput } from '../../components';
import { useIntl } from '../../util/reactIntl';

import css from './FieldPasswordInput.module.css';

const EyeIcon = ({ visible }) =>
  visible ? (
    // Eye-off (password currently visible — clicking will hide)
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    // Eye (password currently hidden — clicking will show)
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

const FieldPasswordInput = props => {
  const { className, ...rest } = props;
  const [visible, setVisible] = useState(false);
  const intl = useIntl();

  return (
    <div className={css.wrapper}>
      <FieldTextInput
        {...rest}
        className={className}
        type={visible ? 'text' : 'password'}
      />
      <button
        type="button"
        className={css.toggle}
        onClick={() => setVisible(v => !v)}
        // mousedown.preventDefault stops the input from blurring when
        // the user clicks the eyeball (which would shift focus away
        // mid-typing on some platforms).
        onMouseDown={e => e.preventDefault()}
        aria-label={intl.formatMessage({
          id: visible
            ? 'FieldPasswordInput.hidePassword'
            : 'FieldPasswordInput.showPassword',
        })}
        aria-pressed={visible}
      >
        <EyeIcon visible={visible} />
      </button>
    </div>
  );
};

export default FieldPasswordInput;
